import { z } from '@hono/zod-openapi';
import { PROBLEM_CONTENT_TYPE, PROBLEM_STATUS, PROBLEM_TITLES, ProblemSchema } from './problem.js';

/**
 * Header `Idempotency-Key` für anlegende Routen ohne Textausgabe ([V] E-p3): 8–128 Zeichen aus `[A-Za-z0-9_-]`.
 * Dieselbe Regel wie die Datenbank (`ck_idempotency_key_key`, `idempotency_key.key`): ein Schlüssel, den der Vertrag
 * annimmt, darf in der Tabelle nie an der CHECK-Regel scheitern (kein `.`, kein `:`).
 */
export const IdempotencyKeyHeader = z.object({
  'idempotency-key': z
    .string()
    .min(8)
    .max(128)
    .regex(/^[A-Za-z0-9_-]+$/),
});

export type ProblemResponse = {
  description: string;
  content: { 'application/problem+json': { schema: typeof ProblemSchema } };
};

const TITLE_BY_STATUS = new Map<number, string>();
for (const code of Object.keys(PROBLEM_STATUS) as (keyof typeof PROBLEM_STATUS)[]) {
  const status = PROBLEM_STATUS[code];
  if (!TITLE_BY_STATUS.has(status)) {
    TITLE_BY_STATUS.set(status, PROBLEM_TITLES[code]);
  }
}

/** Fehlerantworten (`application/problem+json` + `ProblemSchema`) für die genannten Statuswerte. */
export function problemResponses<const Codes extends readonly number[]>(
  codes: Codes,
): Record<Codes[number], ProblemResponse> {
  const out: Record<number, ProblemResponse> = {};
  for (const status of codes) {
    const description = TITLE_BY_STATUS.get(status);
    if (description === undefined) {
      throw new RangeError(`Status ${status} gehört zu keinem ProblemCode`);
    }
    out[status] = {
      description,
      content: { [PROBLEM_CONTENT_TYPE]: { schema: ProblemSchema } },
    };
  }
  return out as Record<Codes[number], ProblemResponse>;
}
